import React from "react";
import { useState } from "react";
import { DateRangePicker } from "rsuite";
import { selectCurrentUser } from "../auth/authSlice";
import { useSelector } from "react-redux";
import { useGetOrdersByTypeQuery } from "../order/orderSlice";
import { useEffect } from "react";
import * as FileSaver from "file-saver";
import XLSX from "sheetjs-style";
import { format } from "date-fns";
import Select from "react-select";
import toast, { Toaster } from "react-hot-toast";

const OrderReports = ({ employeeData }) => {
  const fileType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  const fileExtension = ".xlsx";
  const user = useSelector(selectCurrentUser);

  const [type, setType] = useState("custom");
  const [showDateRange, setShowDateRange] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(0);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [orderData, setOrderData] = useState([]);
  const [checkboxValues, setCheckboxValues] = useState({});
  const [orderHeading, setOrderHeading] = useState([
    { name: "Date", selector: "orderDate" },
    { name: "Initials", selector: "orderTag" },
    { name: "Enquiry Ref.", selector: "enquiry" },
    { name: "Client Name", selector: "customerName" },
    { name: "New Client", selector: "customerStatus" },
    { name: "Amount", selector: "orderAmount" },
  ]);

  const {
    data: orders,
    isLoading,
    isFetching,
    isSuccess,
    isError,
    error,
    refetch,
  } = useGetOrdersByTypeQuery({
    type: type,
    startDate: startDate,
    endDate: endDate,
  });

  useEffect(() => {
    if (orders) {
      const data = orders.ids.map((id) => {
        return orders.entities[id];
      });
      setOrderData(data);
    }
  }, [orders]);

  useEffect(() => {
    const initialCheckboxValues = orderHeading.reduce(
      (values, heading) => ({
        ...values,
        [heading.selector]: true,
      }),
      {}
    );
    setCheckboxValues(initialCheckboxValues);
  }, []);

  function handleDateRangeSelection(event) {
    setType(event.target.value);
    event.target.value === "custom"
      ? setShowDateRange(true)
      : setShowDateRange(false);
  }

  const handleDateRangeChange = (value) => {
    if (value) {
      const [startDate, endDate] = value;
      const formattedStartDate = format(startDate, "yyyy-MM-dd").toString();
      const formattedEndDate = format(endDate, "yyyy-MM-dd").toString();
      setStartDate(formattedStartDate);
      setEndDate(formattedEndDate);
    }
  };

  function handleEmployeeSelection(selectedOption) {
    setSelectedEmployeeId(parseInt(selectedOption.value));
  }

  function handleCheckboxChange(event) {
    const { value, checked } = event.target;
    setCheckboxValues((prevCheckboxValues) => ({
      ...prevCheckboxValues,
      [value]: checked,
    }));
  }

  const checkedValues = Object.keys(checkboxValues).filter(
    (key) => checkboxValues[key]
  );

  async function generateReport() {
    if (startDate === null || endDate === null) {
      toast.error("Please Select a Date Range to generate reports", {
        style: {
          padding: "16px",
          color: "#D93D3D",
          fontSize: "20px",
        },
      });
      return;
    }
    refetch({ type: type, startDate: startDate, endDate: endDate });

    if (isSuccess) {
      const excelData = orderData.filter((order) =>
        selectedEmployeeId !== 0
          ? order.employeeId === selectedEmployeeId
          : order
      );

      const columnsSelcted = excelData.map((order) =>
        checkedValues.reduce((filteredOrder, key) => {
          filteredOrder[key] = order[key];
          return filteredOrder;
        }, {})
      );

      const filteredData = columnsSelcted.map((order) => {
        const filteredOrder = {};
        for (const key in order) {
          const matchingHeader = orderHeading.find(
            (header) => header.selector === key
          );
          const headerName = matchingHeader ? matchingHeader.name : key;
          filteredOrder[headerName] = order[key];
        }
        return filteredOrder;
      });

      const ws = XLSX.utils.json_to_sheet(filteredData);

      const wb = {
        Sheets: { data: ws },
        SheetNames: ["data"],
      };

      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });

      const data = new Blob([excelBuffer], { type: fileType });

      FileSaver.saveAs(data, `orders${Date.now()}.${fileExtension}`);
      setStartDate(null);
      setEndDate(null);
    }
  }

  const options = [
    { value: "0", label: "All", badge: "" },
    ...employeeData
      .slice()
      .sort((a, b) => a.firstName.localeCompare(b.firstName))
      .map((employee) => ({
        value: employee.employeeId,
        label: `${employee.firstName} ${employee.lastName}`,
        badge:
          employee.activeStatus === "Y"
            ? "Active"
            : employee.activeStatus === "R"
            ? "Inactive"
            : "",
      })),
  ];

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <div className="row">
        <div className="col-12 col-md-4 col-lg-3">
          <label htmlFor="timeRangeSelector">Date Range</label>
          <DateRangePicker
            id="timeRangeSelector"
            className="custom-date-range-picker"
            format="dd/MM/yyyy"
            onChange={handleDateRangeChange}
            block
          />
        </div>
        <div className="col-12 col-md-4 col-lg-3">
          <div className="form-group">
            <label htmlFor="employee">Employee</label>
            <Select
              id="employee"
              className="custom-employee-select"
              options={options}
              onChange={handleEmployeeSelection}
              isSearchable={false}
              defaultValue={options.find((option) => option.value === "0")}
              theme={(theme) => ({
                ...theme,
                borderRadius: 5,
                colors: {
                  ...theme.colors,
                  primary25: "#b9c100",
                  primary: "#d7d7d7",
                },
              })}
              getOptionLabel={(option) => (
                <div className="d-flex">
                  <span>{option.label}</span>
                  {option.badge && (
                    <span className={`badge-${option.badge.toLowerCase()}`}>
                      {option.badge}
                    </span>
                  )}
                </div>
              )}
            />
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-6 col-lg-6">
          <div className="list-group mb-2">
            {orderHeading.map((heading) => (
              <label className="list-group-item">
                <input
                  className="form-check-input me-1"
                  type="checkbox"
                  value={heading.selector}
                  checked={checkboxValues[heading.selector] || false}
                  onChange={handleCheckboxChange}
                />
                {heading.name}
              </label>
            ))}
          </div>
          <div className="text-end">
            <button className="btn btn-gradient" onClick={generateReport}>
              Generate Report
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderReports;
